import React from "react";
import {
  AVATAR_SIZE_PX,
  Box,
  cn,
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
  EntityIconTile,
  getInitials,
  Icon,
  resolveEntityColor,
  Text,
  type Color,
  type EntityAvatarSize,
  type EntityIconStyle,
  type GlyphName,
  type IconName,
} from "@salestrak/ui";

import {
  avatarInitialsSource,
  ENTITY_KIND_ICON,
  resolveAvatarColor,
  resolveEntityVisual,
  type EntityData,
} from "#app/lib/entity-visuals";
import { useAppData } from "#app/lib/data";
import { presenceDotProps, usePresence } from "#app/lib/data/presence-provider";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import { StageIcon } from "./stage-icon";

import blackLogo from "#app/assets/images/black-logo.png";
import type { Omittable } from "@salestrak/ui";

export type EntityVisualSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";

// Default size "sm" maps avatars one Avatar size down ("xs") so photo/initials
// circles sit optically level with the lighter icon glyphs at the same box.
const AVATAR_SIZE_MAP: Record<EntityVisualSize, EntityAvatarSize> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "xs",
  md: "default",
  lg: "lg",
  xl: "xl",
};

// Keyed by the RESOLVED avatar size (not the semantic size) so an `avatarSize`
// override scales its initials with the circle it actually renders. Initials
// are bold at every rung — a monogram is an identity mark, not running text,
// and the heaviest weight the stack carries (700) is what keeps it legible
// against the key-color fill.
const AVATAR_TEXT_MAP: Record<
  EntityAvatarSize,
  { size: "2xs" | "xs" | "sm" | "base" | "lg"; weight: "bold" }
> = {
  "2xs": { size: "2xs", weight: "bold" },
  xs: { size: "2xs", weight: "bold" },
  sm: { size: "xs", weight: "bold" },
  default: { size: "sm", weight: "bold" },
  lg: { size: "base", weight: "bold" },
  xl: { size: "lg", weight: "bold" },
};

// Declared as StageIcon's size union (a subset of IconSize) so one map serves
// both Icon and the stage pie glyph — a stage reads at the same visual weight
// as an icon kind.
const ICON_SIZE_MAP: Record<EntityVisualSize, "xs" | "sm" | "md" | "lg" | "xl"> = {
  "2xs": "xs",
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
};

// At the crumb rung a monogram tile runs smaller than its footprint slot. The
// slot keeps the trail rhythm while the letter stays compact beside labels.
const TILE_PX_2XS = 14;
const monogramGlyphSize = (size: EntityVisualSize) =>
  size === "2xs" ? ("2xs" as const) : ICON_SIZE_MAP[size];
const monogramInset = (size: EntityVisualSize) =>
  size === "2xs" ? TILE_PX_2XS : undefined;
// The 14px monogram tile uses a 4px radius so it keeps square shoulders.
const monogramRounded = (size: EntityVisualSize) =>
  size === "2xs" ? ("xs" as const) : undefined;
// The glyph/radius rung a team tile takes when `avatarSize` pins its px: the
// semantic rung whose OWN avatar circle is that size, so a 16px tile carries
// the 14px glyph and 4px radius the 2xs rung already tunes for a small tile.
const MONOGRAM_RUNG_FOR_AVATAR: Record<EntityAvatarSize, EntityVisualSize> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  default: "md",
  lg: "lg",
  xl: "xl",
};

// Shared footprint (px) so avatars, icons, and stage glyphs all occupy the same
// box at a given size and align in rows/columns. Derived from AVATAR_SIZE_PX via
// the same semantic→Avatar-size remap so it can't drift from the avatar circle.
const FOOTPRINT_PX_MAP: Record<EntityVisualSize, number> = {
  "2xs": AVATAR_SIZE_PX[AVATAR_SIZE_MAP["2xs"]],
  xs: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.xs],
  sm: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.sm],
  md: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.md],
  lg: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.lg],
  xl: AVATAR_SIZE_PX[AVATAR_SIZE_MAP.xl],
};

// Logo glyph size class per avatar size — the logo sits inside the circle the
// way an initial would (roughly single-character height), not edge-to-edge.
const SYSTEM_LOGO_CLASS: Record<EntityAvatarSize, string> = {
  "2xs": "size-2.5",
  xs: "size-3",
  sm: "size-3.5",
  default: "size-4",
  lg: "size-5",
  xl: "size-6",
};

// The SalesTrak author sentinel: the brand logo as a centered glyph in the
// primary-colored fallback circle. Domain-owned — the library avatar knows
// nothing about the brand. One black asset, inverted to the fill's ink.
function SystemEntityAvatar({
  size,
  muted,
  invited,
  decorative,
}: Omittable<{
  size: EntityAvatarSize;
  muted?: boolean;
  invited?: boolean;
  decorative?: boolean;
}>) {
  const gray = muted || invited;
  return (
    <EntityAvatar
      size={size}
      muted={muted}
      invited={invited}
      alt={decorative ? undefined : "SalesTrak"}
      aria-hidden={decorative || undefined}
      className={cn(
        "items-center justify-center",
        gray ? "bg-muted" : "bg-primary",
        invited && "opacity-40",
      )}
    >
      <EntityAvatarImage
        src={blackLogo}
        alt={decorative ? "" : "SalesTrak"}
        className={cn(
          // The ink is the fill's foreground: on `primary` (near-black in
          // light, white in dark) the black glyph inverts in light only; on
          // the muted wash it follows the page ink instead.
          "object-contain",
          gray ? "dark:invert" : "invert dark:invert-0",
          SYSTEM_LOGO_CLASS[size],
          gray && "grayscale",
        )}
      />
    </EntityAvatar>
  );
}

export type EntityVisualProps = Omittable<{
  entity: EntityData;
  size?: EntityVisualSize;
  /** Pin the avatar circle to an exact Avatar size, off the semantic map —
   *  avatar kinds only; icon/stage kinds keep the semantic footprint. */
  avatarSize?: EntityAvatarSize;
  /** Override the shared footprint box (px) — rows that align every visual
   *  on a fixed rail (e.g. global search's 28px glyph column). Entity glyphs,
   *  avatars, and stage pies center inside it. Pair with
   *  `avatarSize` when the semantic size's circle would overflow the box. */
  boxSize?: number;
  /** Glyph size for utility-stroke icon kinds only, off the semantic map.
   *  Surfaces that request an unpadded inline glyph can tune its weight.
   *  Identity glyphs and avatars keep the semantic size. Stage pies also keep
   *  it because StageIcon's own px map already
   *  runs a step large (sm = 16px), so a rung up would overshoot. */
  bareIconSize?: (typeof ICON_SIZE_MAP)[EntityVisualSize];
  padded?: boolean;
  system?: boolean;
  /** Live presence dot on member avatars — OPT-IN. Off by default because on
   *  most surfaces (cells, cards, selectors) the avatar identifies a person
   *  and the dot reads as noise. Pass true only where online state is the
   *  point: members/teams rosters, profiles, the header account avatar, and
   *  Owner columns in data tables (whether the owner is reachable is part of
   *  what that column answers). */
  presence?: boolean;
  /** Show the user's TRUE avatar color even for the signed-in viewer.
   *  Default (false) is the reference rule: your own avatar renders themed to
   *  you everywhere; only profile surfaces (the settings avatar editor)
   *  opt into the real color so the picker's choice is visible where it is
   *  made. */
  trueAvatarColor?: boolean;
  /** Render an icon-kind glyph in the muted tone instead of its entity
   *  color — the quiet leading rail for a de-emphasized list (the inbox row's
   *  project glyph). No-op for avatar/stage/monogram kinds, whose identity IS
   *  their color. */
  muted?: boolean;
  /** Layered icon treatment for padded identity glyphs. */
  iconStyle?: EntityIconStyle;
  /** The name is written beside the visual, so a person's avatar takes no
   *  name of its own: with one, a screen reader says the name twice
   *  ("Olive Owner Olive Owner"). */
  decorative?: boolean;
}>;

// A fixed rail centers an avatar-kind visual inside the shared box so avatar
// rows align with identity-glyph rows.
function inRail(node: React.ReactNode, box: number | undefined) {
  if (box == null) return node;
  return (
    <Box
      width={box}
      height={box}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
    >
      {node}
    </Box>
  );
}

function EntityVisualComponent({
  entity,
  size = "sm",
  avatarSize: avatarSizeOverride,
  boxSize: boxSizeOverride,
  bareIconSize,
  padded,
  system,
  presence: presenceEnabled = false,
  trueAvatarColor = false,
  muted = false,
  iconStyle,
  decorative = false,
}: EntityVisualProps) {
  const visual = resolveEntityVisual(entity);
  const { currentUser } = useAppData();

  // Live presence dot for member avatars. The hook runs unconditionally
  // (hooks can't be conditional) — returns undefined for non-user kinds,
  // non-opted-in call sites, unknown members, or while the first presence
  // poll is in flight; Avatar itself suppresses the dot for former/invited
  // avatars where "online" is meaningless.
  const presence = usePresence(
    entity.kind === "user" && presenceEnabled ? entity.id : undefined,
  );
  // Profile images live in the PHOTOS-flagged media store. With the flag off
  // the avatar shows its initials fallback rather than fetching the image.
  // `hasFlag` answers false until flags resolve, so the image is kept while
  // they are in flight — gating on it there would flash initials on every
  // avatar in the app on each load and each workspace switch.
  const { ready, hasFlag } = useFeatureFlags();
  const photosEnabled = !ready || hasFlag(FeatureFlagKey.PHOTOS);

  if (visual.type === "avatar") {
    const avatarSize = avatarSizeOverride ?? AVATAR_SIZE_MAP[size];
    const isMuted = visual.former;
    const isInvited = visual.invited;
    const displayName = system ? "SalesTrak" : visual.label;
    // Derived from the identity text, never the label — the label's "?"
    // sentinel would read as initials. Null = no identity at all: the kind
    // glyph renders on the muted wash instead.
    const initials = getInitials(avatarInitialsSource(visual.user), avatarSize);

    const avatar = system ? (
      <SystemEntityAvatar
        size={avatarSize}
        muted={isMuted}
        invited={isInvited}
        decorative={decorative}
      />
    ) : (
      <EntityAvatar
        size={avatarSize}
        muted={isMuted}
        invited={isInvited}
        alt={decorative ? undefined : displayName}
        aria-hidden={decorative || undefined}
        presence={presence ? presenceDotProps(presence) : undefined}
      >
        {photosEnabled && visual.user?.profileImage ? (
          <EntityAvatarImage
            src={visual.user.profileImage}
            alt={decorative ? "" : displayName}
          />
        ) : null}
        <EntityAvatarFallback
          // Chosen color, else the stable per-user hash color — never the
          // one-brand-color wall. The viewer's OWN avatar stays themed
          // unless a profile surface opts into the true color.
          color={
            !trueAvatarColor && currentUser?.userId === entity.id
              ? undefined
              : resolveAvatarColor(entity.id, visual.user?.avatarColor)
          }
          icon={initials ? undefined : ENTITY_KIND_ICON.user}
        >
          {initials ? (
            <Text
              size={AVATAR_TEXT_MAP[avatarSize].size}
              weight={AVATAR_TEXT_MAP[avatarSize].weight}
              leading="none"
            >
              {initials}
            </Text>
          ) : null}
        </EntityAvatarFallback>
      </EntityAvatar>
    );

    return inRail(avatar, boxSizeOverride);
  }


  // A team is avatar-kind: like a member's circle, its solid key-color tile IS
  // the identity and the mark inside — the name's initial, or a picked glyph
  // in contrast ink — is the distinguisher. A bare letter or glyph is not
  // legible as a team mark and gives a stack nothing to ring, so the tile
  // survives an explicit `padded={false}` and ignores `muted`.
  if (visual.type === "monogram" || (entity.kind === "team" && visual.type === "icon")) {
    // Being avatar-kind, the tile honours `avatarSize` the way a member's
    // circle does: a row that pins its avatars to one mark (global search's
    // 16px rail) gets team tiles at that same px, glyph and radius on the
    // matching rung, instead of a tile that fills whatever box it was given.
    const rung = avatarSizeOverride ? MONOGRAM_RUNG_FOR_AVATAR[avatarSizeOverride] : size;
    return (
      <EntityIconTile
        monogram={visual.type === "monogram" ? visual.letter : undefined}
        icon={visual.type === "icon" ? visual.icon : undefined}
        color={visual.color}
        size={monogramGlyphSize(rung)}
        boxSize={boxSizeOverride ?? FOOTPRINT_PX_MAP[size]}
        tileSize={
          avatarSizeOverride ? AVATAR_SIZE_PX[avatarSizeOverride] : monogramInset(size)
        }
        rounded={monogramRounded(rung)}
        tile="solid"
      />
    );
  }

  // Icon kinds with no glyph (e.g. sources) render label-only — no empty box.
  if (visual.type === "icon" && visual.icon == null) return null;

  const box = boxSizeOverride ?? FOOTPRINT_PX_MAP[size];

  const resolvedPadded =
    padded ??
    (entity.kind === "workflow" ||
      entity.kind === "issueType" ||
      entity.kind === "issueSource");

  // Explicit `padded={false}` selects a Stroke Rounded inline glyph and removes
  // the footprint box, so spacing hugs the glyph. The default identity mode
  // uses a Duotone Rounded glyph in the shared alignment footprint.
  const bareGlyphSize = bareIconSize ?? ICON_SIZE_MAP[size];

  if (padded === false) {
    if (visual.type === "stage") {
      return <StageIcon stage={visual.stage} size={ICON_SIZE_MAP[size]} />;
    }
    return (
      <Icon
        name={visual.icon as GlyphName}
        size={bareGlyphSize}
        color={muted ? "muted" : undefined}
        style={
          !muted && visual.color
            ? { color: resolveEntityColor(visual.color) }
            : undefined
        }
      />
    );
  }

  // Render the pie at the icon-glyph size (not the full footprint) so a stage
  // reads at the same visual weight as icon kinds; the footprint box still sets
  // the shared alignment box. Filling the box made stage pies ~2× every other
  // glyph and overflow compact controls.
  if (visual.type === "stage") {
    return (
      <Box
        width={box}
        height={box}
        direction="row"
        align="center"
        justify="center"
        shrink={false}
      >
        <StageIcon stage={visual.stage} size={ICON_SIZE_MAP[size]} padded={padded} />
      </Box>
    );
  }

  // Identity icon kinds render the canonical Duotone Rounded glyph in the shared
  // footprint. The component keeps its historical `padded` name so call sites
  // do not need a data or API migration.
  if (resolvedPadded) {
    return (
      <EntityIconTile
        icon={visual.icon as GlyphName}
        color={muted ? "muted" : visual.color}
        iconStyle={iconStyle}
        size={ICON_SIZE_MAP[size]}
        boxSize={box}
      />
    );
  }

  return (
    <Box
      width={box}
      height={box}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
    >
      <Icon
        name={visual.icon as GlyphName}
        size={bareGlyphSize}
        color={muted ? "muted" : undefined}
        style={
          !muted && visual.color
            ? { color: resolveEntityColor(visual.color) }
            : undefined
        }
      />
    </Box>
  );
}

EntityVisualComponent.displayName = "EntityVisual";

export const EntityVisual = React.memo(EntityVisualComponent);

// A neutral, non-entity glyph (e.g. an "All projects" rollup) rendered inside the
// EXACT same footprint box as an icon-kind entity visual, so it lines up with
// EntityTag chips in the same list. Unlike a real entity it is NOT padded by
// default — it uses the utility Stroke Rounded style because it is not an
// identity glyph. The footprint box keeps it aligned. Use this instead of a
// bare <Icon> among entity chips.
export function NeutralEntityVisual({
  icon,
  color = "muted",
  size = "sm",
  padded = false,
}: Omittable<{
  icon: IconName;
  color?: Color;
  size?: EntityVisualSize;
  padded?: boolean;
}>) {
  const box = FOOTPRINT_PX_MAP[size];
  if (padded) {
    return (
      <EntityIconTile icon={icon} color={color} size={ICON_SIZE_MAP[size]} boxSize={box} />
    );
  }
  return (
    <Box
      width={box}
      height={box}
      direction="row"
      align="center"
      justify="center"
      shrink={false}
    >
      <Icon name={icon} size={ICON_SIZE_MAP[size]} color={color} />
    </Box>
  );
}
